import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Table, Input, Button, Space, Tag, Tooltip, Modal } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import MaterialButton from '../Buttons/MaterialButton';
import fetchData from '../../functions/fetchData';
import axios from 'axios';

const EmployeeDataTable = ({ 
  onEdit, 
  onView, 
  onDelete, 
  refreshTrigger = 0,
  showActions = true,
  showSearch = true,
  showPagination = true,
  filteredData = null,
  filterStatus = 'All',
  // Multi-selection props
  selectedEmployees = [],
  onSelectEmployee = null,
  onSelectAll = null,
  selectAll = false,
  showSelection = false
}) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch employees data
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await fetchData("employees", null, "id", {});
      
      // Transform the data to ensure profile_photo_url is properly formatted
      const transformedEmployees = (data || []).map(emp => ({
        ...emp,
        profile_photo_url: emp.profile_photo ? 
          `http://localhost:4402/uploads/${emp.profile_photo}` : 
          null
      }));
      
      setEmployees(transformedEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Load employees on component mount and when refreshTrigger changes
  useEffect(() => {
    if (!filteredData) {
      fetchEmployees();
    } else {
      // Use filtered data with proper photo URLs
      const transformedFilteredEmployees = (filteredData || []).map(emp => ({
        ...emp,
        profile_photo_url: emp.profile_photo ? 
          `http://localhost:4402/uploads/${emp.profile_photo}` : 
          null
      }));
      setEmployees(transformedFilteredEmployees);
      setLoading(false);
    }
  }, [refreshTrigger, filteredData]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  // Filter employees based on search term
  const filteredEmployeesData = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      emp.employee_id?.toLowerCase().includes(searchLower) ||
      emp.english_first_name?.toLowerCase().includes(searchLower) ||
      emp.english_last_name?.toLowerCase().includes(searchLower) ||
      emp.thai_first_name?.toLowerCase().includes(searchLower) ||
      emp.thai_last_name?.toLowerCase().includes(searchLower) ||
      emp.phone_number?.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower) ||
      emp.status?.toLowerCase().includes(searchLower)
    );
  });

  // Handle delete with confirmation
  const handleDelete = async (employee) => {
    const fullName = `${employee.english_first_name || ''} ${employee.english_last_name || ''}`.trim();
    
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete ${fullName}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          
          const headers = {
            'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
          };

          // Use the new delete API endpoint: /deletebyid/:tablename/:colname/:colval
          const response = await axios.delete(
            `/deletebyid/employees/employee_id/${employee.employee_id}`,
            { headers }
          );

          if (response.data.success) {
            toast.success(response.data.message || 'Employee deleted successfully!');
          } else {
            toast.warning(response.data.message || 'Delete operation completed with warnings');
          }
          
          await fetchEmployees(); // Refresh the list
          
          if (onDelete) {
            onDelete(employee);
          }
        } catch (error) {
          console.error('Error deleting employee:', error);
          const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete employee';
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Define Ant Design table columns
  const columns = [
    showSelection && {
      title: (
        <input
          type="checkbox"
          className="form-check-input"
          checked={selectAll}
          onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
        />
      ),
      key: 'selection',
      width: 50,
      fixed: 'left',
      render: (_, record) => {
        const isSelected = selectedEmployees.some(emp => emp.id === record.id);
        return (
          <input
            type="checkbox"
            className="form-check-input"
            checked={isSelected}
            onChange={(e) => onSelectEmployee && onSelectEmployee(record, e.target.checked)}
          />
        );
      }
    },
    {
      title: 'Employee ID',
      dataIndex: 'employee_id',
      key: 'employee_id',
      width: 120,
      fixed: 'left',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => onView && onView(record)}
          style={{ padding: 0, fontWeight: 'bold' }}
        >
          {text}
        </Button>
      ),
      sorter: (a, b) => (a.employee_id || '').localeCompare(b.employee_id || '')
    },
    {
      title: 'Name',
      key: 'name',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {`${record.english_first_name || ''} ${record.english_last_name || ''}`.trim()}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {`${record.thai_first_name || ''} ${record.thai_last_name || ''}`.trim()}
          </div>
        </div>
      ),
      sorter: (a, b) => {
        const nameA = `${a.english_first_name || ''} ${a.english_last_name || ''}`.trim();
        const nameB = `${b.english_first_name || ''} ${b.english_last_name || ''}`.trim();
        return nameA.localeCompare(nameB);
      }
    },
    {
      title: 'Position',
      dataIndex: 'position_name',
      key: 'position',
      width: 150,
      responsive: ['md'],
      render: (text, record) => text || record.position_id || 'N/A',
      sorter: (a, b) => (a.position_name || a.position_id || '').localeCompare(b.position_name || b.position_id || '')
    },
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department',
      width: 150,
      responsive: ['lg'],
      render: (text, record) => text || record.department_id || 'N/A',
      sorter: (a, b) => (a.department_name || a.department_id || '').localeCompare(b.department_name || b.department_id || '')
    },
    {
      title: 'Hire Date',
      dataIndex: 'hire_date',
      key: 'hire_date',
      width: 120,
      responsive: ['md'],
      render: (text) => text ? new Date(text).toLocaleDateString('en-GB') : 'N/A',
      sorter: (a, b) => {
        const dateA = a.hire_date ? new Date(a.hire_date).getTime() : 0;
        const dateB = b.hire_date ? new Date(b.hire_date).getTime() : 0;
        return dateA - dateB;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        let color = 'default';
        if (status === 'Active') color = 'success';
        else if (status === 'Probation') color = 'warning';
        else if (status === 'Inactive') color = 'default';
        else color = 'error';
        
        return <Tag color={color}>{status}</Tag>;
      },
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Probation', value: 'Probation' },
        { text: 'Inactive', value: 'Inactive' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: 'Phone',
      dataIndex: 'phone_number',
      key: 'phone',
      width: 130,
      responsive: ['sm'],
      render: (text) => text || 'N/A'
    },
    showActions && {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Employee">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit && onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Employee">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ].filter(Boolean);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading employees...</p>
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <i className="zmdi zmdi-account-box-o" style={{ fontSize: '4rem', color: '#ccc' }}></i>
        </div>
        <h5 className="text-muted">No employees found</h5>
        <p className="text-muted">No employee records are available in the database.</p>
      </div>
    );
  }

  return (
    <div className="employee-data-table">
      {/* Search Bar */}
      {showSearch && (
        <div style={{ marginBottom: 16 }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Input
              placeholder="Search employees..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: 300 }}
              allowClear
            />
            <span style={{ color: '#888' }}>
              Total: {filteredEmployeesData.length} employee(s)
            </span>
          </Space>
        </div>
      )}

      {/* Ant Design Table */}
      <Table
        columns={columns}
        dataSource={filteredEmployeesData}
        rowKey="id"
        loading={loading}
        pagination={showPagination ? {
          current: currentPage,
          pageSize: itemsPerPage,
          total: filteredEmployeesData.length,
          onChange: (page) => setCurrentPage(page),
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
          position: ['bottomCenter']
        } : false}
        scroll={{ x: 1200 }}
        size="small"
        bordered
      />
    </div>
  );
};

export default EmployeeDataTable;
